import React, { useState, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useIntl } from 'react-intl';
import css from './SeoPage.module.css'; // Use local SeoPage styles

// Memoized article grid card component adapted for new Strapi data structure
const ArticleGridCard = React.memo(
  ({ article, getShortDescription, intl, urlParts, history, preview }) => {
    const { pageType, urlPath, section = [], tag = [] } = article.attributes || {};
    const firstSection = section[0] || {};
    const articleTitle =
      firstSection.title ||
      urlPath
        ?.split('/')
        .pop()
        ?.split('-')
        .map(word => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
        .join(' ') ||
      'Article';
    // Use preview as fallback if firstSection.paragraph is empty
    const articleDescription = firstSection.paragraph || preview || '';

    const handleClick = () => {
      if (article.attributes?.urlPath) {
        const routePath = `/${urlParts?.[0] || 'en-US'}${
          article.attributes.urlPath.startsWith('/') ? '' : '/'
        }${article.attributes.urlPath}`;
        history.push(routePath);
      }
    };

    return (
      <div className={css.articleFlex}>
        <div className={css.articleRight}>
          <div className={css.gridTitle}>{articleTitle}</div>
          <div className={css.gridDescription}>{getShortDescription(articleDescription)}</div>
          <div className={css.readMore} onClick={handleClick}>
            {intl.formatMessage({ id: 'General.read.more', defaultMessage: 'Read more' })}
          </div>
        </div>
      </div>
    );
  }
);

const ArticleSection = props => {
  const { urlParts, sectionTitle, titleNotOnImageTexts: articleType = [], preview } = props || {};
  // Use preview prop as fallback if firstSection.paragraph is empty

  const [visibleCount, setVisibleCount] = useState(6);
  const intl = useIntl();
  const history = useHistory();

  // Memoized function to get truncated description (same as ArticlesList)
  const getShortDescription = useCallback(description => {
    const text = description || '';
    if (text.length <= 150) return text;

    // Cut at 400 chars but find the last space to avoid cutting words
    const truncated = text.slice(0, 400);
    const lastSpace = truncated.lastIndexOf(' ');

    return lastSpace > 0 ? truncated.slice(0, lastSpace) + '...' : truncated + '...';
  }, []);

  // Memoized load more button handler
  const handleLoadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + 4, articleType.length));
  }, [articleType.length]);

  // Memoized displayed articles
  const displayedArticles = useMemo(() => articleType?.slice(0, visibleCount), [
    articleType,
    visibleCount,
  ]);

  // Early return if no articles
  if (!Array.isArray(articleType) || articleType.length === 0) {
    return null;
  }

  return (
    <section className={css.articleList}>
      <div>
        <h2 className={css.disciplineHeading}>{displayedArticles.length ? sectionTitle : ''}</h2>
        <div className={css.articleGrid}>
          {(displayedArticles.length ? displayedArticles : [])?.map(article => (
            <ArticleGridCard
              key={article?.id}
              article={article}
              getShortDescription={getShortDescription}
              intl={intl}
              urlParts={urlParts}
              history={history}
              preview={preview}
            />
          ))}
        </div>

        {articleType.length > 6 && visibleCount < articleType.length && (
          <div className={css.articleLoadButton}>
            <button onClick={handleLoadMore}>
              {intl.formatMessage({
                id: 'General.more.stories',
                defaultMessage: 'Load more stories',
              })}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

ArticleSection.defaultProps = {
  rootClassName: null,
  className: null,
  titleNotOnImageTexts: [],
  sectionTitle: null,
};

export default React.memo(ArticleSection);
